import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {IAccessTokenResponse} from '@evencel/models';
import {snackBarError} from '@evencel/ui';
import {Store} from '@ngxs/store';
import {BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError} from 'rxjs';
import {Logout} from '../../states';
import {HandleTokenResponse} from '../../states/app/app.actions';
import {AppState} from '../../states/app/app.state';
import {LoginService} from '../login/login.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    matSnackBar = inject(MatSnackBar);
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(private store: Store, private loginService: LoginService) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const accessToken = this.store.selectSnapshot(AppState.accessToken);

        let clone = req.clone();

        if (accessToken) {
            clone = this.addTokenHeader(req, accessToken);
        }

        return next.handle(clone).pipe(
            catchError((error: HttpErrorResponse) => {
                if (req.url.includes('auth/logout') && error.status === 400) {
                    // this.store.dispatch(new SignOut());
                    this.store.dispatch(new Logout());
                }
                // We don't want to refresh token for some requests like login or refresh token itself
                // So we verify url and we throw an error if it's the case
                if (req.url.includes('auth/refresh-token') || req.url.includes('auth/login')) {
                    // We do another check to see if refresh token failed
                    // In this case we want to logout user and to redirect it to login page
                    if (req.url.includes('auth/refresh-token')) {
                        snackBarError(error.error.message, this.matSnackBar);
                        this.store.dispatch(new Logout());
                    }

                    return throwError(() => error);
                }

                if (error.status === 401) {
                    return this.handle401Error(req, next);
                }

                return throwError(() => error);
            })
        );
    }

    private handle401Error(request: HttpRequest<unknown>, next: HttpHandler) {
        if (this.isRefreshing) {
            // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
            // – which means the new token is ready and we can retry the request again
            return this.refreshTokenSubject.pipe(
                filter(result => result !== null),
                take(1),
                switchMap((accessToken: string | null) => {
                    if (accessToken) {
                        return next.handle(this.addTokenHeader(request, accessToken));
                    }
                    throw new Error('Null access token');
                })
            );
        } else {
            this.isRefreshing = true;
            // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
            this.refreshTokenSubject.next(null);
            const refreshToken = this.store.selectSnapshot(AppState.refreshToken) as string;

            return this.loginService.refreshToken({refreshToken}).pipe(
                switchMap((response: IAccessTokenResponse) => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(response.accessToken);
                    this.store.dispatch(new HandleTokenResponse(response));
                    return next.handle(this.addTokenHeader(request, response.accessToken));
                }),
                catchError((res: HttpErrorResponse) => this.logout(res))
            );
        }
    }

    private logout(error: HttpErrorResponse) {
        this.isRefreshing = false;
        this.store.dispatch(new Logout());
        return throwError(() => error);
    }

    private addTokenHeader(request: HttpRequest<unknown>, accessToken: string) {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }
}
